// Test de force en séance — protocole des trois montées, formule de Brzycki, et
// estimation initiale du 1RM quand le client n'en a encore aucun.
//
// Module PUR : aucun accès réseau, aucun état, aucun composant. Tout ce qui est ici
// se vérifie à la main et se rejoue depuis `scripts/check-strength-test.mjs`.
//
// ⚠️ VOCABULAIRE — même règle que `strengthStandards.ts` : « Débutant / Intermédiaire /
// Avancé » désignent le niveau de PROGRAMME du client (`wizard_submissions.level_id`),
// jamais un palier des tables ExRx. Les paliers gardent leurs noms anglais
// (`novice` / `intermediate` / `advanced`).

import {
  RM_FIELD_BY_MOVEMENT,
  standardForBodyweight,
  pullUpTotalFromRatio,
  type StrengthMovement,
  type StandardsGender,
  type AgeBracket,
  type StandardTier,
  type BeginnerExperience,
} from "@/lib/strengthStandards"
import { RATIO_MAP, type AthleteLevel, type AthleteOneRm } from "@/lib/exerciseNormalization"

// ── Formule de Brzycki ──────────────────────────────────────

/**
 * `1RM = poids × (36 / (37 − répétitions))`.
 *
 * Fiable jusqu'à une dizaine de répétitions. Au-delà elle dérive, et c'est ASSUMÉ :
 * la série de test vise 3 répétitions, mais un client qui échoue très tard en fera
 * peut-être 8 ou 12 — on applique alors la formule telle quelle, sans plafond ni
 * correction inventée. Une estimation imprécise vaut mieux qu'une absence de donnée.
 *
 * Deux seules bornes, toutes deux mathématiques et non « prudentielles » :
 *   — en dessous d'une répétition il n'y a pas de série à convertir ;
 *   — à 37 répétitions le dénominateur s'annule (et devient négatif au-delà), la
 *     formule ne renvoie plus un nombre. Ce n'est pas un garde-fou de confort,
 *     c'est le domaine de définition.
 *
 * À 1 répétition, `36 / (37 − 1) = 1` : le 1RM est le poids soulevé, sans cas
 * particulier à coder. C'est exactement la situation du client qui échoue dès la
 * première répétition de sa série max.
 */
export function estimateOneRmBrzycki(weightKg: number, reps: number): number | null {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return null
  if (!Number.isFinite(reps) || reps < 1) return null
  if (reps >= 37) return null
  return weightKg * (36 / (37 - reps))
}

/** Au-delà, `estimateOneRmBrzycki` reste calculé mais la valeur est à prendre avec des pincettes. */
export const BRZYCKI_RELIABLE_REPS = 10

/**
 * Inverse de Brzycki : pourcentage du 1RM qu'on peut espérer tenir `reps` fois.
 * `pct = 100 × (37 − reps) / 36`. Sert à proposer la charge de la série max
 * (≈ 94 % pour 3 répétitions) — proposer autre chose reviendrait à contredire,
 * dès l'écran, la formule qui servira à lire le résultat.
 */
export function percentOfOneRmForReps(reps: number): number {
  return ((37 - reps) / 36) * 100
}

// ── Protocole des trois montées ─────────────────────────────

export interface TestSetSpec {
  /** 1, 2 ou 3 — le numéro affiché au client. */
  index: number
  reps: number
  /** % du 1RM de référence. Sur la série max c'est une PROPOSITION, pas une consigne. */
  percentOfOneRm: number
  maxEffort: boolean
  /** Repos qui suit CETTE série, en secondes. */
  restSec: number
}

/** Objectif de répétitions de la série max — aussi la valeur pré-remplie du formulaire. */
export const MAX_EFFORT_TARGET_REPS = 3

/**
 * Repos croissant avec l'intensité de la série qui vient d'être faite : 1 min après la
 * série 1, 2 min après la série 2, 3 min après la série max — donc aussi 3 min avant
 * d'attaquer le mouvement suivant, puisque c'est le même repos.
 */
const REST_AFTER_SET_SEC = [60, 120, 180]

/**
 * Trois montées progressives, identiques en Débutant et Intermédiaire, plus courtes en
 * Avancé (un athlète confirmé s'échauffe en moins de répétitions et arrive plus frais
 * sur la série qui compte).
 */
export function testProtocolForLevel(level: AthleteLevel): TestSetSpec[] {
  const [warmupReps, buildReps] = level === "advanced" ? [5, 3] : [7, 5]
  return [
    { index: 1, reps: warmupReps, percentOfOneRm: 70, maxEffort: false, restSec: REST_AFTER_SET_SEC[0] },
    { index: 2, reps: buildReps, percentOfOneRm: 85, maxEffort: false, restSec: REST_AFTER_SET_SEC[1] },
    {
      index: 3,
      reps: MAX_EFFORT_TARGET_REPS,
      percentOfOneRm: Math.round(percentOfOneRmForReps(MAX_EFFORT_TARGET_REPS)),
      maxEffort: true,
      restSec: REST_AFTER_SET_SEC[2],
    },
  ]
}

/**
 * Protocole déclaré par le JSON du programme (`exercises[].test_protocol`), converti et
 * validé. La clé JSON est la SOURCE DE VÉRITÉ du contenu : ajuster le protocole d'un
 * programme doit se faire dans son fichier, pas dans le code.
 *
 * Retourne null au moindre défaut — clé absente, série manquante, nombre non fini — et
 * l'appelant retombe alors sur `testProtocolForLevel`. Un protocole à moitié lu serait
 * pire que pas de protocole du tout : le client verrait des charges incohérentes sans
 * rien pour s'en apercevoir.
 */
export function parseTestProtocol(raw: unknown): TestSetSpec[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null

  const specs: TestSetSpec[] = []
  for (const item of raw) {
    if (typeof item !== "object" || item == null) return null
    const o = item as Record<string, unknown>
    const reps = Number(o.reps)
    const percent = Number(o.percent_1rm)
    const restSec = Number(o.rest_sec)
    const index = Number(o.set)
    if (!Number.isFinite(reps) || reps < 1) return null
    if (!Number.isFinite(percent) || percent <= 0) return null
    if (!Number.isFinite(restSec) || restSec < 0) return null
    specs.push({
      index: Number.isFinite(index) && index > 0 ? index : specs.length + 1,
      reps,
      percentOfOneRm: percent,
      maxEffort: o.max_effort === true,
      restSec,
    })
  }
  // Une série maximale, et une seule : c'est elle qui produit le 1RM. Sans elle le test
  // ne mesure rien ; avec deux, on ne saurait pas laquelle enregistrer.
  if (specs.filter((sp) => sp.maxEffort).length !== 1) return null
  if (!specs[specs.length - 1].maxEffort) return null
  return specs
}

// ── Quel mouvement de référence est testé par cet exercice ──

/** Colonne 1RM d'`athlete_profiles` → mouvement des tables. Inverse de `RM_FIELD_BY_MOVEMENT`. */
const MOVEMENT_BY_RM_FIELD = new Map<string, StrengthMovement>(
  (Object.entries(RM_FIELD_BY_MOVEMENT) as [StrengthMovement, string][]).map(([m, f]) => [f, m])
)

export interface TestedLift {
  movement: StrengthMovement
  /** Colonne 1RM d'`athlete_profiles` mise à jour par ce test. */
  rmField: keyof AthleteOneRm
  /** % du 1RM de référence que représente le 1RM de CE mouvement (`RATIO_MAP`). */
  ratio: number
  /** La charge manipulée est le poids d'UN seul haltère. */
  perDumbbell: boolean
  /** La charge manipulée est le poids TOTAL du système : poids de corps ± lest/assistance. */
  bodyweightBased: boolean
  /** L'exercice EST le mouvement de référence (ratio 100, mesure directe et non dérivée). */
  isDirectMeasure: boolean
}

/**
 * Mouvement de référence testé par un exercice de séance, ou null si l'exercice ne
 * mesure rien de connu (le parcours de test guidé se replie alors sur l'affichage
 * d'exercice normal, il ne bloque jamais la séance).
 *
 * On réutilise `RATIO_MAP` — la table qui sert DÉJÀ à convertir un %1RM en kg — plutôt
 * qu'une liste de noms propre au test. C'est ce qui garantit que la charge proposée aux
 * séries 1 et 2 et le 1RM relu de la série 3 parlent de la même chose : le second est
 * l'inversion exacte du premier. Une table séparée dériverait dès la première
 * divergence de nommage.
 *
 * Conséquence assumée : un test fait sur une variante (Goblet Squat, Dumbbell Floor
 * Press, Lat Pulldown…) remonte un 1RM de référence DÉRIVÉ par le ratio, pas mesuré.
 * C'est moins précis qu'une barre, mais c'est la seule donnée disponible pour un client
 * qui s'entraîne à la maison — et c'est déjà, en sens inverse, ce qui lui prescrit ses
 * charges tous les jours. `isDirectMeasure` permet de le signaler à l'écran.
 */
export function resolveTestedLift(canonicalName: string): TestedLift | null {
  const entry = RATIO_MAP[canonicalName]
  if (!entry) return null
  const movement = MOVEMENT_BY_RM_FIELD.get(entry.base)
  if (!movement) return null
  return {
    movement,
    rmField: entry.base,
    ratio: entry.ratio,
    perDumbbell: entry.perDumbbell === true,
    bodyweightBased: entry.bw === true,
    isDirectMeasure: entry.ratio === 100 && !entry.perDumbbell,
  }
}

/**
 * 1RM de référence (unité d'`athlete_profiles`) déduit du 1RM mesuré sur le mouvement
 * réellement testé. Inversion exacte du calcul de charge de `computeChargeKg`, qui fait
 * `charge = 1RM_référence × ratio / 100 × pct / 100`.
 *
 * L'unité d'entrée suit celle du mouvement : kg à la barre, kg d'UN haltère si
 * `perDumbbell`, poids total du système (corps ± lest) si `bodyweightBased`. Celle de
 * sortie suit toujours `athlete_profiles` — `rm_pull_up` y est une charge TOTALE, jamais
 * un lest seul (cf. `pullUpRatioFromTotal`).
 */
export function referenceOneRmFromLift(liftOneRmKg: number, ratio: number): number | null {
  if (!Number.isFinite(liftOneRmKg) || liftOneRmKg <= 0) return null
  if (!Number.isFinite(ratio) || ratio <= 0) return null
  return (liftOneRmKg * 100) / ratio
}

// ── Estimation initiale : le tout premier test ──────────────

/**
 * Facteur de prudence appliqué au palier Intermediate pour un Débutant qui a déclaré
 * une reprise récente. Mémoire musculaire et récupération nerveuse rapides justifient
 * de partir du palier au-dessus, mais pas de le prendre plein pot sur une première
 * séance de test. Valeur à recalibrer en test terrain.
 */
export const RECENT_RETURN_CAUTION_FACTOR = 0.85

/**
 * Facteur appliqué au palier Novice pour un client qui n'a jamais pratiqué. Le palier
 * Novice d'ExRx suppose déjà plusieurs mois d'entraînement : le prendre tel quel comme
 * point de départ absolu poserait des charges nettement trop lourdes dès la série 1.
 */
export const NEVER_TRAINED_FACTOR = 0.5

function tierAndFactor(
  level: AthleteLevel,
  beginnerExperience: BeginnerExperience | null
): { tier: StandardTier; factor: number } {
  if (level === "advanced") return { tier: "advanced", factor: 1 }
  if (level === "intermediate") return { tier: "intermediate", factor: 1 }

  switch (beginnerExperience) {
    case "reprise_recente":
      return { tier: "intermediate", factor: RECENT_RETURN_CAUTION_FACTOR }
    case "reprise_ancienne":
      return { tier: "novice", factor: 1 }
    case "vrai_debutant":
      return { tier: "novice", factor: NEVER_TRAINED_FACTOR }
    default:
      // Questionnaire d'expérience non renseigné (inscription antérieure à sa mise en
      // service). On retombe sur l'hypothèse la PLUS PRUDENTE, pas sur une moyenne :
      // une première série trop légère ne coûte qu'un peu de temps, une première série
      // trop lourde coûte une blessure.
      console.warn(
        "[strengthTest] questionnaire d'expérience Débutant absent — estimation initiale " +
          `repliée sur l'hypothèse la plus prudente (palier Novice × ${NEVER_TRAINED_FACTOR})`
      )
      return { tier: "novice", factor: NEVER_TRAINED_FACTOR }
  }
}

/**
 * 1RM de départ, en kg et dans l'unité d'`athlete_profiles`, pour un client qui n'a
 * encore jamais testé ce mouvement.
 *
 * ⚠️ Cette valeur ne sert QU'À calculer les charges des séries 1 et 2 du tout premier
 * test. Elle n'est jamais enregistrée comme un 1RM : `rm_history` ne reçoit que le
 * résultat du calcul de Brzycki sur la série max. Ne pas la réutiliser ailleurs.
 */
export function initialOneRmEstimateKg(params: {
  movement: StrengthMovement
  gender: StandardsGender
  bracket: AgeBracket
  bodyweightKg: number
  level: AthleteLevel
  beginnerExperience: BeginnerExperience | null
}): number | null {
  const { movement, gender, bracket, bodyweightKg, level, beginnerExperience } = params
  if (!Number.isFinite(bodyweightKg) || bodyweightKg <= 0) return null

  const { tier, factor } = tierAndFactor(level, beginnerExperience)
  const standard = standardForBodyweight(movement, gender, bracket, bodyweightKg, tier)
  if (standard == null) return null

  // Les tables donnent un RATIO de charge totale pour la traction et des kg pour les
  // mouvements à la barre. `athlete_profiles` stocke des kg dans les deux cas.
  const kg =
    movement === "traction_lestee" ? pullUpTotalFromRatio(standard, bodyweightKg) : standard
  if (kg == null || kg <= 0) return null

  return kg * factor
}

/**
 * Les cinq 1RM à utiliser pendant le test : la valeur réelle du client quand elle
 * existe, l'estimation initiale sinon. Rendu sous la forme d'un `AthleteOneRm` pour
 * être passé tel quel à `computeChargeKg` — les charges des séries 1 et 2 sont ainsi
 * calculées par le MÊME code que toutes les autres charges de l'app.
 *
 * `estimated` liste les mouvements dont la valeur est une estimation, pour que l'écran
 * puisse le dire au client plutôt que de lui présenter un chiffre inventé comme acquis.
 */
export function buildTestReferenceOneRm(params: {
  real: AthleteOneRm | null
  gender: StandardsGender
  bracket: AgeBracket
  bodyweightKg: number | null
  level: AthleteLevel
  beginnerExperience: BeginnerExperience | null
}): { oneRm: AthleteOneRm; estimated: Set<StrengthMovement> } {
  const { real, gender, bracket, bodyweightKg, level, beginnerExperience } = params

  const oneRm: AthleteOneRm = {
    rm_bench_press: real?.rm_bench_press ?? null,
    rm_back_squat: real?.rm_back_squat ?? null,
    rm_deadlift: real?.rm_deadlift ?? null,
    rm_overhead_press: real?.rm_overhead_press ?? null,
    rm_pull_up: real?.rm_pull_up ?? null,
  }
  const estimated = new Set<StrengthMovement>()

  for (const [movement, field] of Object.entries(RM_FIELD_BY_MOVEMENT) as [
    StrengthMovement,
    keyof AthleteOneRm,
  ][]) {
    const current = oneRm[field]
    if (typeof current === "number" && Number.isFinite(current) && current > 0) continue
    if (bodyweightKg == null) continue

    const estimate = initialOneRmEstimateKg({
      movement,
      gender,
      bracket,
      bodyweightKg,
      level,
      beginnerExperience,
    })
    if (estimate == null) continue

    oneRm[field] = estimate
    estimated.add(movement)
  }

  return { oneRm, estimated }
}
